import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Users, Phone, Shield, Clock, MapPin, Award, Heart, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const PHONE_NUMBER = "306-262-5995";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_emergency-locksmith-4/artifacts/dcw292bm_ChatGPT%20Image%20Mar%2027%2C%202026%2C%2010_11_56%20PM.png";

const AboutPage = () => {
  const values = [
    { icon: <Heart className="w-8 h-8" />, title: "Family Values", desc: "We treat every customer like family - with respect, honesty, and care." },
    { icon: <Shield className="w-8 h-8" />, title: "Trust & Integrity", desc: "Honest pricing, quality work, and no hidden fees. Ever." },
    { icon: <Clock className="w-8 h-8" />, title: "Reliability", desc: "When we say we'll be there, we'll be there. 24/7, 365 days a year." },
    { icon: <Award className="w-8 h-8" />, title: "Excellence", desc: "Licensed, insured, and committed to the highest standards." },
  ];

  const serviceAreas = [
    "Saskatoon", "Warman", "Martensville", "Osler", "Dalmeny", "Dundurn",
    "Langham", "Clavet", "Aberdeen", "Delisle", "Hepburn", "Colonsay"
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-900/80 border border-blue-700 rounded-sm px-6 py-3 mb-6">
                <Users className="w-6 h-6 text-amber-400" />
                <span className="text-white font-semibold">Local Family-Owned Business</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl text-white mb-6">
                About <span className="text-amber-400">306 Locksmith</span>
              </h1>
              <p className="text-slate-300 text-lg mb-6">
                We're a local, family-owned locksmith business proudly serving Saskatoon and surrounding communities. Our mission is simple: provide fast, reliable, and honest locksmith services at fair prices.
              </p>
              <p className="text-slate-400 mb-8">
                We understand that lock and key emergencies can happen at the worst times. That's why we're available 24/7 - because your security can't wait.
              </p>
              <a href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`}>
                <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: {PHONE_NUMBER}
                </Button>
              </a>
            </div>
            <div className="flex justify-center">
              <img src={LOGO_URL} alt="306 Locksmith" className="w-64 h-64 object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-4 md:px-8 bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl text-white text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <div key={idx} className="bg-slate-700 border border-slate-600 rounded-sm p-6 text-center">
                <div className="text-amber-400 mb-4 flex justify-center">{value.icon}</div>
                <h3 className="text-white font-bold text-xl mb-2">{value.title}</h3>
                <p className="text-slate-400">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl text-white text-center mb-12">Why Saskatoon Trusts Us</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-bold text-lg">Licensed & Insured</h3>
                  <p className="text-slate-400">Fully licensed professionals with complete insurance coverage.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-bold text-lg">Upfront Pricing</h3>
                  <p className="text-slate-400">No surprises - we quote before we start, and that's what you pay.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-bold text-lg">24/7 Emergency Service</h3>
                  <p className="text-slate-400">Locked out at 3 AM? We're here. No extra fees for nights or weekends.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-bold text-lg">Senior Friendly</h3>
                  <p className="text-slate-400">Patient, respectful service with special consideration for seniors.</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-sm p-8">
              <h3 className="font-heading text-xl text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-400" />
                Service Areas
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {serviceAreas.map((area) => (
                  <div key={area} className="flex items-center gap-2 text-slate-300">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    {area}
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-sm mt-4">+ All surrounding communities</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 md:px-8 bg-amber-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-slate-900 mb-4">Ready to Work With Us?</h2>
          <p className="text-slate-700 text-lg mb-8">Call now for fast, friendly service you can trust.</p>
          <a href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`}>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xl px-10 py-6">
              <Phone className="w-6 h-6 mr-3" />
              {PHONE_NUMBER}
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
